import { action, observable } from 'mobx';
import {
    createUser,
    getDefaultGroup,
    getSystemContact,
    getUserById,
    getUserPermissions,
    getUsersParentGroups,
    updateUser,
    updateUserPermissions,
    updateUsersParentGroups
} from '../AppShell/Authentication/AuthorisationAsync';
import {
    GroupIGroup,
    UserICreateNewUser,
    UserIEditParentGroups,
    UserIEditUser,
    UserIParentGroup,
    UserIReplaceUserPermissions,
    UserIUser,
    UserIUserPermissions
} from '../AppShell/Authentication/AuthorisationModels';
import { displayError, displayMessage } from '../Config/ApplicationConfig';
import { IGuidResult } from '../Shared/HttpRequestExecutor';
import { Maybe } from '../Shared/Types';

export class UserFileStore {
    @observable public formError = '';

    @action
    public setFormError = (error: string): void => {
        this.formError = error;
    };

    public loadUserInformation =
        (userId?: string) =>
        async (): Promise<{
            user: Maybe<UserIUser>;
            groups: UserIParentGroup[];
            systemContact: Maybe<UserIUser>;
            defaultGroup: GroupIGroup | undefined;
        }> => {
            try {
                const [user, groups, systemContact, defaultGroup] = await Promise.all([
                    userId ? getUserById(userId) : Promise.resolve(undefined),
                    userId ? getUsersParentGroups(userId) : Promise.resolve([]),
                    this.loadSystemContact(),
                    this.loadDefaultGroup()
                ]);

                return {
                    user,
                    groups: groups ?? [],
                    systemContact,
                    defaultGroup
                };
            } catch (err) {
                console.error('Failed to load user: ', err);
                if (typeof err === 'string') {
                    displayError(err);
                }
                throw new Error('Unable to load user information');
            }
        };

    public loadUserPermissions = (userId: string) => async (): Promise<UserIUserPermissions | undefined> => {
        return await getUserPermissions(userId);
    };

    public updatePermissions = async (userId: string, permissions: UserIReplaceUserPermissions): Promise<boolean> => {
        try {
            await updateUserPermissions(userId, permissions);
            displayMessage('User permissions updated');
            return true;
        } catch (err) {
            console.error('Failed to update permissions - err: ', err);
            if (typeof err === 'string') {
                displayError(err);
            }
            return false;
        }
    };

    public saveUserInformation =
        (userId?: string) =>
        async (user: UserIUser): Promise<string> => {
            if (!userId) {
                const newUser: UserICreateNewUser = {
                    familyName: user.familyName,
                    givenNames: user.givenNames,
                    email: user.email,
                    username: user.username,
                    enabled: user.enabled,
                    isSystemContact: user.isSystemContact,
                    authoringSystem: user.authoringSystem,
                    authoringSystemForeignKey: user.authoringSystemForeignKey
                };

                const result: IGuidResult = await createUser(newUser);
                userId = result.id;
            } else {
                const updateForUser: UserIEditUser = {
                    familyName: user.familyName,
                    givenNames: user.givenNames,
                    email: user.email,
                    username: user.username,
                    enabled: user.enabled,
                    isSystemContact: user.isSystemContact
                };

                await updateUser(userId, updateForUser);
            }

            const updateUserGroups: UserIEditParentGroups = {
                permissionOwnerId: userId,
                authoringSystem: user.authoringSystem,
                authoringSystemForeignKey: user.authoringSystemForeignKey,
                groups: user.groups.map(group => ({
                    id: group.id,
                    name: group.name,
                    authoringSystem: group.authoringSystem,
                    authoringSystemForeignKey: group.authoringSystemForeignKey
                }))
            };

            await updateUsersParentGroups(userId, updateUserGroups);

            return userId;
        };

    public loadDefaultGroup = async (): Promise<GroupIGroup | undefined> => {
        try {
            return await getDefaultGroup();
        } catch (err) {
            console.error('Failed to get default group = ', err);
            return;
        }
    };

    public loadSystemContact = async (): Promise<UserIUser | undefined> => {
        try {
            return await getSystemContact();
        } catch (err) {
            console.error('Failed to get system contact = ', err);
            if (err instanceof Error) {
                displayError(err.message);
            }
            return;
        }
    };
}
